// AIPanel.jsx
import React from "react";
import "./AIPanel.scss";

import { PanelControl, IconControl } from "spark-core-dx/components";
import { AISplitPane } from "./AISplitPane"; // adjust path as needed

const AIPanel = ({
  open,
  className,
  presentation = "slideout",
  children,                  // goes to LEFT "Manual"
  workspaceHeader = null,
  title,
  onCancel,
  width,
  onUpdatePanel,
  onBeforeSend,
  onSessionChange,
  onOpenAssociatedOutput,
  onAnimationEnd,
  mapToClass,
  panelStorageKey = "ai-panel:width",
  shareLink,
  breakoutLink,
  session,
  aiChats,
  referenceId,
  referenceName,
  params,
  operationName,
  sideBarClassName = "side-navbar",
  minPanelWidth = 360,
  minLeft = 360,
  minRight = 360,
  minMiddle = 360,
  splitPaneStorageKey,
  initialLeftPct,
  initialRightPct,
  enableContentDragHiding = true,
  chatEnabled = true,
  chatPaneToggleEnabled = true,
  chatPane = "right",        // 'right' | 'middle' | 'left'
  chatPaneCollapseMode = "auto", // 'auto' | 'disabled' | 'open' | 'closed'
  chatPaneStartOpen = null,
  directModeOnly = false,
  aiProcessingMode = "Choose",
  defaultOrchestrationMode = null,
  singleSessionMode = false,
  planningPreviewEnabled = true,
  previewPanelEnabled = false,
  previewRefreshEnabled = null,
  splashMessage = null,
  introMessage = null,
  startChat = null,
  payload = null,
  reviewContext = null,
  contentReady = true,
  loadingContent = null,
  bottomDrawerEnabled = false,
  bottomDrawerDefaultOpen = false,
  bottomDrawerTitle = "Debug",
  bottomDrawerContent = null,
  // optional pane content
  left,
  middle,
  right
}) => {
  const isScreenWorkspace = presentation === "screen-workspace";
  const handleShare = (link) => {
    navigator.clipboard.writeText(link);
    // header toasts live inside AISplitPane; if you want a top-level toast here, we can lift state.
  };
  const handleBreakout = (link) => window.open(link, "_blank");
  const handleOnExit = async () => {
    if (onCancel) await onCancel();
  };

  const manualContent = isScreenWorkspace ? (
    <div className="ai-panel-screen-workspace__manual">
      {workspaceHeader ? (
        <div className="ai-panel-screen-workspace__header">
          {workspaceHeader}
        </div>
      ) : null}
      <div className="ai-panel-screen-workspace__body">
        {children}
      </div>
    </div>
  ) : children;

  const splitPane = (
    <AISplitPane
      className={className}
      onUpdatePanel={onUpdatePanel}
      onBeforeSend={onBeforeSend}
      onSessionChange={onSessionChange}
      onOpenAssociatedOutput={onOpenAssociatedOutput}
      session = {session}
      aiChats={aiChats}
      referenceId={referenceId}
      referenceName={referenceName}
      params={params}
      operationName={operationName}
      enableContentDragHiding={enableContentDragHiding}
      chatEnabled={chatEnabled}
      chatPaneToggleEnabled={chatPaneToggleEnabled}
      chatPane={chatPane}
      chatPaneCollapseMode={chatPaneCollapseMode}
      chatPaneStartOpen={chatPaneStartOpen}
      directModeOnly={directModeOnly}
      aiProcessingMode={aiProcessingMode}
      defaultOrchestrationMode={defaultOrchestrationMode}
      singleSessionMode={singleSessionMode}
      planningPreviewEnabled={planningPreviewEnabled}
      previewPanelEnabled={previewPanelEnabled}
      previewRefreshEnabled={previewRefreshEnabled}
      payload={payload}
      reviewContext={reviewContext}
      left={left}
      middle={middle}
      right={right}
      storageKey={splitPaneStorageKey}
      initialLeftPct={initialLeftPct ?? (isScreenWorkspace ? 0.5 : undefined)}
      initialRightPct={initialRightPct ?? (isScreenWorkspace ? 0.5 : undefined)}
      minLeft = {minLeft}
      minMiddle = {minMiddle}
      minRight = {minRight}
      splashMessage = {splashMessage}
      introMessage = {introMessage}
      startChat = {startChat}
      bottomDrawerEnabled={bottomDrawerEnabled}
      bottomDrawerDefaultOpen={bottomDrawerDefaultOpen}
      bottomDrawerTitle={bottomDrawerTitle}
      bottomDrawerContent={bottomDrawerContent}
    >
      {manualContent}
    </AISplitPane>
  );

  const panelContent = contentReady === false
    ? (
      <div className="ai-panel-deferred-content">
        {loadingContent}
      </div>
    )
    : splitPane;

  if (presentation === "inline" || isScreenWorkspace) {
    if (open === false) return null;

    return (
      <div
        className={`panel__content ai-panel-content-scope${
          isScreenWorkspace ? " ai-panel-content-scope--screen-workspace" : ""
        }`}
      >
        {splitPane}
      </div>
    );
  }

  return (
    <PanelControl
      open={open}
      onClose={handleOnExit}
      onAnimationEnd={onAnimationEnd}
      title={title}
      rootClassName="ai-panel"
      className={className ?? ""}
      mapToClass={mapToClass}
      position="right"
      minWidth={minPanelWidth}
      width={width}
      resizable={true}
      storageKey={panelStorageKey}
      sideBarClassName={sideBarClassName}
      contentClassName="ai-panel-content-scope"
      resizeOverlay = {enableContentDragHiding}
      headerActions={
        <>
          {shareLink && <IconControl iconKey={"share"} title={"Share"} onClick={() => handleShare(shareLink)} />}
          {breakoutLink && (
            <IconControl iconKey={"opennewwindow"} title={"Open in new window"} onClick={() => handleBreakout(breakoutLink)} />
          )}
        </>
      }
    >
      {panelContent}
    </PanelControl>
  );
};

export default AIPanel;
